import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { message, Badge, Descriptions, Image, Button } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";

import PageHeader from "../../components/shared/PageHeader"
import BlueButton from "../../components/shared/BlueButton";
import LoadingModal from "../../components/shared/LoadingModal";
import SendDocumentsModal from "../../components/shared/SendDocumentsModal";

import { getEquipment, sendDocumentLinks } from "../../api/equipment";
import { getFileUrl } from "../../api/file";
import { cn, formatDate } from "../../lib/utils";


export default function EquipmentView() {
    const { equipmentId } = useParams();
    const [isLoading, setLoading] = useState(false);
    const [equipment, setEquipment] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [showSendModal, setShowSendModal] = useState(false);
    const [loadingSendDocuments, setLoadingSendDocuments] = useState(false);
    const navigate = useNavigate();

    const fetchEquipment = async ({ equipmentId }) => {
        setLoading(true);
        try {
            const result = await getEquipment(equipmentId);
            setEquipment(result)
            if (result.attachments && result.attachments.length > 0) {
                updateDocuments(result)
            }
        } catch (err) {
            console.error(err);
            message.error("Something went wrong");
        }
        setLoading(false)
    }

    const sendDocuments = async (contactData) => {
        setLoadingSendDocuments(true);
        try {
            const data = await sendDocumentLinks(equipment.id, contactData);

            let sentToInfo = data.sentTo?.contact || "contact";
            if (data.sentTo?.phone) {
                sentToInfo = `${data.sentTo.contact} (${data.sentTo.phone})`;
            } else if (data.sentTo?.email) {
                sentToInfo = `${data.sentTo.contact} (${data.sentTo.email})`;
            }

            const deliveryMethod = contactData.deliveryMethod === "sms" ? "SMS" : "Email";
            message.success(
                `Document link sent via ${deliveryMethod} to ${sentToInfo}.`
            );

            // Close modal
            setShowSendModal(false);
        }
        catch (err) {
            console.error(err);
            message.error(err.message || "Failed to send document links.");
        }
        finally {
            setLoadingSendDocuments(false);
        }
    }

    useEffect(() => {
        fetchEquipment({ equipmentId });

    }, [equipmentId]);


    const updateDocuments = (equipment) => {
        const docs = [];

        equipment?.attachments.forEach(
            (doc, index) => {
                if (doc.isActive) {
                    docs.push(
                        {
                            key: `${doc.id + index}`,
                            label: "Document Name",
                            children: doc.fileAttachment.name
                        },
                        {
                            key: `${doc.id + index}`,
                            label: "Expiry Date",
                            children: formatDate(doc.fileAttachment.expireAt),
                        },
                        {
                            key: `${doc.id + index}`,
                            label: "Document",
                            children: doc.fileAttachment.file.name
                        }
                    );
                }
            }
        );
        setDocuments(docs);
    }


    const items = !equipment ? [] : [
        {
            key: "1",
            label: "Equipment Name",
            children: equipment.name || "",
        },
        {
            key: "2",
            label: "Nickname",
            children: equipment.nickName || "",
        },
        {
            key: "3",
            label: "Equipment Type",
            children: <div className="capitalize">{equipment.type}</div>,
        },
        {
            key: "4",
            label: "Make",
            children: equipment.make || "N/A",
            span: 2
        },

        {
            key: "5",
            label: "Model / Size",
            children: equipment.model || "N/A",
            span: 2
        },
        {
            key: "7",
            label: "Status",
            children: <Badge status="processing" text={<span className="uppercase">{equipment.status}</span>} />,
            span: 3
        },

        {
            key: "8",
            label: "Rate(AUD)",
            children: "$" + equipment.rate.rate,
        },
        {
            key: "9",
            label: "Rate by",
            children: <div className="capitalize">{equipment.rate.rateBy}</div>,
            span: 2,
        },

        {
            key: "10",
            label: "Description",
            children: equipment.description || "N/A",
            span: 3
        },
        {
            key: "11",
            label: "Attachments",
            labelStyle: { border: "0" },
            contentStyle: { backgroundColor: "rgba(0, 0, 0, 0.02)" },
            span: 3,
        },

        ...documents
    ]

    if (!equipment) return <LoadingModal />
    return (
        <main className="space-y-6 min-h-full flex flex-col">
            <PageHeader home="Equipments" route="/equipments" title={equipment.name} />

            <div className={cn("flex-grow bg-white ml-6 p-6 relative",
                !equipment.avatars && "pt-20")
            }>

                <section className="space-y-6">
                    {equipment.avatars && equipment.avatars.length > 0 &&
                        <Image.PreviewGroup >
                            {equipment.avatars.map((avatar) => (
                                <Image
                                    key={avatar.id}
                                    src={getFileUrl(avatar)}
                                    width={245}
                                    height={245}
                                    style={{ objectFit: "cover", }}
                                />
                            ))}
                        </Image.PreviewGroup>
                    }
                    <Descriptions items={items} className="rounded-none" bordered />
                </section>

                <div className="absolute right-6 top-5 flex gap-2">
                    <Button
                        className="flex items-center"
                        onClick={() => setShowSendModal(true)}
                        disabled={!equipment.attachments || equipment.attachments.length === 0}
                    >
                        <PlusOutlined />
                        Send Documents
                    </Button>
                    <BlueButton className="flex items-center" type="primary" onClick={() => navigate(`/equipments/form/${equipment.id}`)}>
                        <EditOutlined />
                        Edit Equipment
                    </BlueButton>
                </div>

                {isLoading && <LoadingModal />}
            </div>

            <SendDocumentsModal
                open={showSendModal}
                onCancel={() => setShowSendModal(false)}
                onSubmit={sendDocuments}
                loading={loadingSendDocuments}
            />
        </main>
    )
}
